<template>
    <Card class="conditions-settings">
        <Heading
            :labelTitle="$t.__('settings.sellsymodule.invoicing.conditions.title')"
            variant="h2"
            :prefixColoredIcon="{ icon: 'document', color: 'brand' }"
        />
        <ConditionsSettingsLoader v-if="isLoading" />
        <div v-else class="conditions-settings__content">
            <div class="conditions-settings__content-form">
                <div class="conditions-settings__content-form-note">
                    <InvoiceTermsEditor
                        v-model="notes"
                        :title="$t.__('settings.sellsymodule.invoicing.conditions.note_title')"
                        :languages="languages"
                    />
                    <p
                        class="typographies-paragraph-small-regular conditions-settings__content-form-note-caption"
                    >
                        {{ $t.__('settings.sellsymodule.invoicing.conditions.note_caption') }}
                    </p>
                </div>
                <div class="conditions-settings__content-form-late-penalty">
                    <InvoiceTermsEditor
                        v-model="latePenaltyText"
                        :title="
                            $t.__('settings.sellsymodule.invoicing.conditions.late_penalty_title')
                        "
                        :languages="languages"
                    />
                    <CompassCheckbox
                        :modelValue="showLatePenaltyWarningText"
                        :checked="showLatePenaltyWarningText"
                        :label="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_label',
                            )
                        "
                        :caption="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_caption',
                            )
                        "
                        @change="showLatePenaltyWarningText = $event"
                    />
                </div>
                <div class="conditions-settings__content-form-vat">
                    <InvoiceTermsEditor
                        v-model="vatLawText"
                        :title="$t.__('settings.sellsymodule.invoicing.conditions.vat_title')"
                        :languages="languages"
                    />
                    <CompassCheckbox
                        :modelValue="showVatLawText"
                        :checked="showVatLawText"
                        :label="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_label',
                            )
                        "
                        :caption="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_caption',
                            )
                        "
                        @change="showVatLawText = $event"
                    />
                </div>
                <div class="conditions-settings__content-form-terms-conditions">
                    <InvoiceTermsEditor
                        v-model="termsAndConditionsText"
                        :title="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.terms_conditions_title',
                            )
                        "
                        :languages="languages"
                    />
                    <CompassFormItem
                        :label="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.terms_conditions_form_item_label',
                            )
                        "
                        :info="
                            $t.__(
                                'settings.sellsymodule.invoicing.conditions.law_text_checkbox_caption',
                            )
                        "
                    >
                        <CompassCheckboxGroup
                            v-model="termsAndConditionsOnDocument"
                            :direction="CompassEnums.ChoicesListPosition.HORIZONTAL"
                        >
                            <CompassCheckbox
                                :modelValue="DocumentType.ESTIMATE"
                                :label="$t.__('common.labels.estimates')"
                            />
                            <CompassCheckbox
                                :modelValue="DocumentType.ORDER"
                                :label="$t.__('common.labels.orders')"
                            />
                            <CompassCheckbox
                                :modelValue="DocumentType.DELIVERY"
                                :label="$t.__('common.labels.deliveries')"
                            />
                            <CompassCheckbox
                                :modelValue="DocumentType.INVOICE"
                                :label="$t.__('common.labels.invoices')"
                            />
                            <CompassCheckbox
                                :modelValue="DocumentType.CREDIT_NOTE"
                                :label="$t.__('common.labels.creditnotes')"
                            />
                            <CompassCheckbox
                                :modelValue="DocumentType.PROFORMA"
                                :label="$t.__('common.labels.proforma')"
                            />
                        </CompassCheckboxGroup>
                    </CompassFormItem>
                </div>
                <LabelConfirmationInput
                    v-model="conditions"
                    v-model:showConditions="showConditions"
                    :uiLangs="uiLangs"
                    :hadConditionFile="hadConditionFile"
                />
                <CompassButton
                    class="conditions-settings__content-form-submit"
                    :label="$t.__('common.actions.save')"
                    :loading="isSaving"
                    @click="handleSave"
                />
            </div>
            <div class="conditions-settings__content-preview">
                <Heading
                    :labelTitle="$t.__('settings.sellsymodule.invoicing.conditions.preview_title')"
                    variant="h3"
                    :prefixColoredIcon="{ icon: 'help-circle-fill', color: 'brand' }"
                />
                <div class="conditions-settings__content-preview-pdf-container">
                    <PdfPreview
                        :showLatePenaltyWarningText="showLatePenaltyWarningText"
                        :showVatLawText="showVatLawText"
                        :termsAndConditionsOnDocument="termsAndConditionsOnDocument"
                    />
                </div>
                <p
                    class="conditions-settings__content-preview-caption typographies-paragraph-medium-regular"
                >
                    {{ $t.__('settings.sellsymodule.invoicing.conditions.preview_caption') }}
                </p>
            </div>
        </div>
    </Card>
    <CompassModal
        v-model:visible="displayConfirmationModal"
        :size="CompassEnums.ModalSize.SMALL"
        :title="$t.__('settings.sellsymodule.invoicing.conditions.confirmation_modal.title')"
        :primaryAction="{
            label: $t.__(
                'settings.sellsymodule.invoicing.conditions.confirmation_modal.confirm_btn',
            ),
            action: save,
        }"
        :secondaryActions="[
            {
                label: $t.__('common.actions.cancel'),
                action: () => (displayConfirmationModal = false),
            },
        ]"
        @closed="displayConfirmationModal = false"
    >
        <p class="typographies-paragraph-medium-regular conditions-settings__modal-description">
            {{ $t.__('settings.sellsymodule.invoicing.conditions.confirmation_modal.description') }}
        </p>
        <CompassAlert
            :description="
                $t.__('settings.sellsymodule.invoicing.conditions.confirmation_modal.alert_text')
            "
            :type="CompassEnums.AlertType.NEUTRAL"
            :showIcon="false"
            :showClose="false"
            class="conditions-settings__modal-alert"
        />
    </CompassModal>
</template>

<script setup lang="ts">
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import PdfPreview from 'vcomponents/settings/invoicing/conditions/PdfPreview.vue';
import { inject, onMounted, ref } from 'vue';
import {
    getMetadata,
    getConditionsAndAcceptances,
    updateConditionsAndAcceptances,
} from 'api/settings/invoicing/InvoicingSettingsService';

import { DocumentType } from 'types/api/settings/invoicing/invoicingSettingsTypes';
import type { InvoicingSettingsMetadata } from 'types/api/settings/invoicing/invoicingSettingsTypes';
import type {
    ConditionTranslation,
    TranslatableText,
} from 'types/api/settings/invoicing/invoicingConditionsTypes';
import ConditionsSettingsLoader from 'vcomponents/settings/invoicing/conditions/ConditionsSettingsLoader.vue';
import InvoiceTermsEditor from 'vcomponents/settings/invoicing/conditions/InvoiceTermsEditor.vue';
import LabelConfirmationInput from 'vcomponents/settings/invoicing/conditions/LabelConfirmationInput.vue';
import { PushToast, Translator } from 'js/shims-vue';
import { captureException } from '@sentry/vue';

const $pushToast = inject('$pushToast') as PushToast;
const $t = inject('$t') as Translator;

const emptyTranslatableText = (): TranslatableText => ({ value: '', translations: [] });

const languages = ref<InvoicingSettingsMetadata['languages']>([]);
const uiLangs = ref<InvoicingSettingsMetadata['uiLangs']>([]);
const isLoading = ref<boolean>(true);
const isSaving = ref<boolean>(false);

const notes = ref<TranslatableText>(emptyTranslatableText());
const latePenaltyText = ref<TranslatableText>(emptyTranslatableText());
const showLatePenaltyWarningText = ref<boolean>(false);
const vatLawText = ref<TranslatableText>(emptyTranslatableText());
const showVatLawText = ref<boolean>(false);
const termsAndConditionsText = ref<TranslatableText>(emptyTranslatableText());
const termsAndConditionsOnDocument = ref<DocumentType[]>([]);
const showConditions = ref<boolean>(false);
const conditions = ref<ConditionTranslation[]>([]);
const displayConfirmationModal = ref(false);
let hadConditionFile: boolean = false;

const fetchData = async (): Promise<void> => {
    try {
        const [metadata, settings] = await Promise.all([
            getMetadata(),
            getConditionsAndAcceptances(),
        ]);

        languages.value = metadata.languages;
        uiLangs.value = metadata.uiLangs;

        notes.value = settings.notes ?? emptyTranslatableText();
        latePenaltyText.value = settings.latePenaltyText ?? emptyTranslatableText();
        showLatePenaltyWarningText.value = settings.showLatePenaltyWarningText ?? false;
        vatLawText.value = settings.vatLawText ?? emptyTranslatableText();
        showVatLawText.value = settings.showVatLawText ?? false;
        termsAndConditionsText.value = settings.termsAndConditionsText ?? emptyTranslatableText();
        termsAndConditionsOnDocument.value = settings.showTermsAndConditionsTextOn ?? [];
        showConditions.value = settings.showConditions ?? false;
        conditions.value = settings.conditions;
        hadConditionFile = conditions.value.some((condition) => !!condition.file);
        isLoading.value = false;
    } catch (error) {
        captureException(error);
        $pushToast({
            type: 'error',
            text: $t.__('settings.sellsymodule.invoicing.conditions.toast_error_data_fetch'),
        });
    }
};

const handleSave = async (): Promise<void> => {
    if (hadConditionFile && conditions.value.every((condition) => !condition.file)) {
        displayConfirmationModal.value = true;
        return;
    }
    await save();
};

const save = async (): Promise<void> => {
    isSaving.value = true;
    displayConfirmationModal.value = false;
    try {
        await updateConditionsAndAcceptances({
            notes: notes.value,
            latePenaltyText: latePenaltyText.value,
            showLatePenaltyWarningText: showLatePenaltyWarningText.value,
            vatLawText: vatLawText.value,
            showVatLawText: showVatLawText.value,
            termsAndConditionsText: termsAndConditionsText.value,
            showTermsAndConditionsTextOn: termsAndConditionsOnDocument.value,
            showConditions: showConditions.value,
            conditions: conditions.value.map((condition) => ({
                fileId: condition.file?.id ?? null,
                label: condition.label,
                lang: condition.lang,
            })),
        });
        hadConditionFile = conditions.value.some((condition) => !!condition.file);
        $pushToast({
            type: 'success',
            text: $t.__('common.labels.settings_saved'),
        });
    } catch (error) {
        captureException(error);
        $pushToast({
            type: 'error',
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isSaving.value = false;
    }
};

onMounted(async () => {
    await fetchData();
});
</script>

<style lang="scss" scoped>
.conditions-settings {
    p {
        margin: 0;
    }
    &__content {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: var(--spacing-2);

        @media screen and (max-width: 1199px) {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-6);
            .conditions-settings__content-preview {
                width: 50%;
                align-self: center;
            }
        }

        &-form {
            grid-column: span 8;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-5);
            &-note-caption:is(p) {
                color: var(--colors-common-text-subtle-default);
                margin-top: var(--spacing-0);
            }
            &-submit {
                align-self: end;
            }
            :deep(.tiptap.ProseMirror) {
                height: 59px;
                max-height: 59px;
            }
        }
        &-form-late-penalty,
        &-form-vat {
            .compass-checkbox {
                margin-top: var(--spacing-4);
            }
        }

        &-form-terms-conditions {
            .compass-form-item {
                margin-top: var(--spacing-4);
            }
            :deep(.tiptap.ProseMirror) {
                height: 220px;
                max-height: 220px;
            }
        }

        &-preview {
            grid-column: span 4;
            &-pdf-container {
                padding: var(--spacing-3);
                border-radius: var(--spacing-0);
                background-color: var(--colors-common-background-subtle);
                margin-bottom: var(--spacing-3);
                overflow: hidden;

                svg {
                    display: block;
                    width: 100%;
                    height: auto;
                }
            }
            &-caption {
                color: var(--colors-common-text-subtle-default);
            }
        }
    }
    &__modal-description:is(p) {
        margin: 0 0 var(--spacing-3);
    }
    &__modal-alert {
        margin-bottom: 0;
    }
}
</style>
